/**
 * StandaloneChat — Floating chat overlay for in-game messaging
 *
 * Small overlay (not a UIManager modal) that uses the same ChatPanel.
 * Created on demand when the notification bubble is tapped during a game.
 *
 * Layout: a header with two tabs (CHAT / FAV CREW) + close, then the content:
 *  - CHAT: conversation avatar rail on the left, ChatPanel on the right. The
 *    peer's identity appears ONCE — in the ChatPanel's own header row.
 *  - FAV CREW: the player's favourite friends; tapping one opens a chat with
 *    them (added to the rail as a draft until a message is exchanged).
 * z-index: 600 (below notification bubble at 700)
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { ChatPanel } from './ChatPanel';
import type { ChatService } from '../../../services/ChatService';
import type { Conversation, StoredMessage } from '../../../db/ChatDatabase';

// ─── Constants ────────────────────────────────────────────────────────

const STYLE_ID = 'standalone-chat-styles';

// ─── Types ────────────────────────────────────────────────────────────

interface StandaloneChatEvents {
  'close': void;
  /** Peer identity tapped in the chat header — open their player card. */
  'profile:tap': { playerId: string; username?: string; picture?: string };
}

export interface FavCrewEntry {
  id: string;
  username: string;
  picture?: string;
  isOnline?: boolean;
}

export interface StandaloneChatOptions {
  container: HTMLElement;
  chatService: ChatService;
  localPlayerId: string;
  /**
   * Conversation to open on mount. Omit (chat-bubble path always passes one,
   * the in-game menu does not) to open on the most-recent conversation, or an
   * empty "no conversations yet" state when the player has none.
   */
  peerId?: string;
  peerUsername?: string;
  peerPicture?: string;
  /** Resolve a peer's freshest identity from the crew list (avatar/name). */
  resolvePeer?: (id: string) => { username?: string; picture?: string } | undefined;
  /** Favourite friends for the FAV CREW tab (freshest crew-list data). */
  getFavourites?: () => FavCrewEntry[];
}

// ─── Component ────────────────────────────────────────────────────────

export class StandaloneChat extends EventEmitter<StandaloneChatEvents> {
  public element: HTMLElement;
  private chatPanel: ChatPanel | null = null;
  private chatService: ChatService;
  private localPlayerId: string;
  private activePeerId: string;
  private activePeerUsername: string;
  private activePeerPicture: string;
  private resolvePeer?: (id: string) => { username?: string; picture?: string } | undefined;
  private getFavourites?: () => FavCrewEntry[];
  /** Identity passed by the opener — only valid for the initially-opened peer. */
  private optionPeerId: string;
  private optionUsername: string;
  private optionPicture: string;
  private container: HTMLElement;

  // DOM refs
  private sidebarEl: HTMLElement;
  private chatWrapEl: HTMLElement;
  private favesEl: HTMLElement;
  private bodyEl: HTMLElement;

  // Conversations
  private conversations: Conversation[] = [];

  // Bound handlers for cleanup
  private messageHandler: ((msg: StoredMessage) => void) | null = null;
  private messageUpdatedHandler: ((msg: StoredMessage) => void) | null = null;
  private conversationsHandler: ((convos: Conversation[]) => void) | null = null;

  constructor(options: StandaloneChatOptions) {
    super();
    this.container = options.container;
    this.chatService = options.chatService;
    this.localPlayerId = options.localPlayerId;
    this.activePeerId = options.peerId ?? '';
    this.activePeerUsername = options.peerUsername ?? 'Player';
    this.activePeerPicture = options.peerPicture ?? '';
    this.resolvePeer = options.resolvePeer;
    this.getFavourites = options.getFavourites;
    this.optionPeerId = options.peerId ?? '';
    this.optionUsername = options.peerUsername ?? '';
    this.optionPicture = options.peerPicture ?? '';

    this.injectStyles();

    // Build DOM
    this.element = this.createElement();
    this.sidebarEl = this.element.querySelector('.standalone-chat-sidebar')!;
    this.chatWrapEl = this.element.querySelector('.standalone-chat-chatwrap')!;
    this.favesEl = this.element.querySelector('.standalone-chat-faves')!;
    this.bodyEl = this.element.querySelector('.standalone-chat-body')!;

    // Mount
    this.container.appendChild(this.element);

    // Load sidebar conversations then open initial chat
    this.loadConversationsAndOpen(options.peerId);

    // Setup live listeners
    this.setupServiceListeners();

    // Entrance animation
    requestAnimationFrame(() => {
      this.element.classList.add('visible');
    });
  }

  private createElement(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'standalone-chat';

    el.innerHTML = `
      <div class="standalone-chat-header">
        <button class="standalone-chat-tab active" data-tab="chat">CHAT</button>
        <button class="standalone-chat-tab" data-tab="faves">FAV CREW</button>
        <button class="standalone-chat-close">&times;</button>
      </div>
      <div class="standalone-chat-content">
        <div class="standalone-chat-chatwrap">
          <div class="standalone-chat-sidebar"></div>
          <div class="standalone-chat-body"></div>
        </div>
        <div class="standalone-chat-faves" style="display:none;"></div>
      </div>
    `;

    el.querySelector('.standalone-chat-close')!.addEventListener('click', () => {
      this.close();
    });

    el.querySelectorAll<HTMLElement>('.standalone-chat-tab').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.switchTab(btn.dataset.tab === 'faves' ? 'faves' : 'chat');
      });
    });

    return el;
  }

  private switchTab(tab: 'chat' | 'faves'): void {
    this.element.querySelectorAll<HTMLElement>('.standalone-chat-tab').forEach((btn) => {
      btn.classList.toggle('active', btn.dataset.tab === tab);
    });
    this.chatWrapEl.style.display = tab === 'chat' ? 'flex' : 'none';
    this.favesEl.style.display = tab === 'faves' ? 'flex' : 'none';
    if (tab === 'faves') this.renderFaves();
  }

  private async loadConversationsAndOpen(peerId?: string): Promise<void> {
    // A caller-specified peer with no existing conversation becomes a draft:
    // visible in the rail immediately, dropped on close if nothing was sent.
    if (peerId) {
      this.chatService.openDraft(
        peerId,
        this.resolvePeer?.(peerId)?.username || this.optionUsername,
        this.resolvePeer?.(peerId)?.picture || this.optionPicture,
      );
    }
    this.conversations = await this.chatService.getConversations();
    this.renderSidebar();
    // Menu-launched (no peer): fall back to the most-recent conversation, or an
    // empty state when the player has never chatted. Bubble-launched always
    // passes the sender, so it opens straight into that thread.
    const target = peerId || this.conversations[0]?.peerId;
    if (target) {
      await this.openConversation(target);
    } else {
      this.renderEmptyState();
    }
  }

  /** Shown when opened from the menu with zero existing conversations. */
  private renderEmptyState(): void {
    this.activePeerId = '';
    if (this.chatPanel) {
      this.chatPanel.destroy();
      this.chatPanel = null;
    }
    this.bodyEl.innerHTML = `<div class="standalone-chat-empty">No conversations yet.<br>Pick someone in Fav Crew or start one from a player's card.</div>`;
  }

  private async openConversation(peerId: string): Promise<void> {
    this.activePeerId = peerId;

    // Find conversation info. Prefer the crew list (freshest avatar/name),
    // then any caller-provided identity, then stored conversation metadata.
    const convo = this.conversations.find(c => c.peerId === peerId);
    const resolved = this.resolvePeer?.(peerId);
    // Opener-provided identity only applies to the peer it was opened for.
    const optUser = peerId === this.optionPeerId ? this.optionUsername : '';
    const optPic = peerId === this.optionPeerId ? this.optionPicture : '';
    this.activePeerUsername = resolved?.username || convo?.peerUsername || optUser || 'Player';
    this.activePeerPicture = resolved?.picture || convo?.peerPicture || optPic || '';

    // Destroy existing panel
    if (this.chatPanel) {
      this.chatPanel.destroy();
      this.chatPanel = null;
    }

    // Create new panel — its header row is the ONE place the peer is shown.
    this.chatPanel = new ChatPanel({
      localPlayerId: this.localPlayerId,
      peerId,
      peerUsername: this.activePeerUsername,
      peerPicture: this.activePeerPicture,
    });

    // Wire send
    this.chatPanel.on('send', ({ content, type }) => {
      this.chatService.sendMessage(peerId, this.activePeerUsername, this.activePeerPicture, content, type);
    });

    // Photo attach
    this.chatPanel.on('image', () => {
      void this.chatService.sendPhoto(peerId, this.activePeerUsername, this.activePeerPicture);
    });

    // Tap the peer identity in the panel header → their player card.
    this.chatPanel.on('profile:tap', ({ playerId }) => {
      this.emit('profile:tap', {
        playerId,
        username: this.activePeerUsername,
        picture: this.activePeerPicture,
      });
    });

    // Trash → wipe this conversation's local history.
    this.chatPanel.on('clear', async () => {
      await this.chatService.deleteConversation(peerId);
      this.chatPanel?.setMessages([]);
    });

    this.bodyEl.innerHTML = '';
    this.bodyEl.appendChild(this.chatPanel.element);

    // Load messages
    const messages = await this.chatService.getMessages(peerId, 50);
    this.chatPanel.setMessages(messages);

    // Mark as read
    this.chatService.markRead(peerId);

    // Update sidebar active state
    this.updateSidebarActive();
  }

  private renderSidebar(): void {
    this.sidebarEl.innerHTML = '';

    for (const convo of this.conversations) {
      const avatar = document.createElement('div');
      avatar.className = `chat-sidebar-avatar ${convo.peerId === this.activePeerId ? 'active' : ''}`;
      avatar.dataset.peerId = convo.peerId;

      const resolved = this.resolvePeer?.(convo.peerId);
      const picture = resolved?.picture || convo.peerPicture;
      const initial = (resolved?.username || convo.peerUsername).charAt(0).toUpperCase();
      if (picture) {
        avatar.innerHTML = `<img src="${picture}" alt="${initial}">`;
      } else {
        avatar.innerHTML = `<span>${initial}</span>`;
      }

      // Unread badge
      if (convo.unreadCount > 0) {
        const badge = document.createElement('span');
        badge.className = 'chat-sidebar-badge';
        badge.textContent = convo.unreadCount > 99 ? '99+' : String(convo.unreadCount);
        avatar.appendChild(badge);
      }

      avatar.addEventListener('click', () => {
        this.openConversation(convo.peerId);
      });

      this.sidebarEl.appendChild(avatar);
    }
  }

  /** FAV CREW tab — favourite friends; tap one to start (or resume) a chat. */
  private renderFaves(): void {
    this.favesEl.innerHTML = '';
    const faves = this.getFavourites?.() ?? [];

    if (faves.length === 0) {
      this.favesEl.innerHTML = `<div class="standalone-chat-empty">No faves yet.<br>Tap the heart on a friend in My Crew.</div>`;
      return;
    }

    for (const fave of faves) {
      const row = document.createElement('button');
      row.className = 'standalone-chat-fave-row';
      const initial = (fave.username || 'P').charAt(0).toUpperCase();
      row.innerHTML = `
        <span class="standalone-chat-fave-avatar">${fave.picture
          ? `<img src="${fave.picture}" alt="">`
          : `<span>${initial}</span>`}</span>
        <span class="standalone-chat-fave-name">${fave.username}</span>
        ${fave.isOnline ? '<span class="standalone-chat-fave-online"></span>' : ''}
      `;
      row.addEventListener('click', async () => {
        this.chatService.openDraft(fave.id, fave.username, fave.picture ?? '');
        this.conversations = await this.chatService.getConversations();
        this.renderSidebar();
        this.switchTab('chat');
        await this.openConversation(fave.id);
      });
      this.favesEl.appendChild(row);
    }
  }

  private updateSidebarActive(): void {
    this.sidebarEl.querySelectorAll('.chat-sidebar-avatar').forEach((el) => {
      const htmlEl = el as HTMLElement;
      htmlEl.classList.toggle('active', htmlEl.dataset.peerId === this.activePeerId);
    });
  }

  private setupServiceListeners(): void {
    // Live incoming messages
    this.messageHandler = (msg: StoredMessage) => {
      if (this.chatPanel && (msg.peerId === this.activePeerId || msg.from === this.activePeerId)) {
        this.chatPanel.appendMessage(msg);
        if (msg.from !== this.localPlayerId) {
          this.chatService.markRead(this.activePeerId);
        }
      }
    };
    this.chatService.on('message:received', this.messageHandler as any);

    // A stored message changed in place (photo blob finished downloading).
    this.messageUpdatedHandler = (msg: StoredMessage) => {
      this.chatPanel?.updateMessage(msg);
    };
    this.chatService.on('message:updated', this.messageUpdatedHandler as any);

    // Conversation list updates (new messages, read status changes)
    this.conversationsHandler = (convos: Conversation[]) => {
      this.conversations = convos;
      this.renderSidebar();
    };
    this.chatService.on('conversations:updated', this.conversationsHandler as any);
  }

  close(): void {
    // Peers opened but never messaged disappear from the rail again.
    void this.chatService.clearEmptyDrafts();
    this.element.classList.remove('visible');
    setTimeout(() => {
      // Emit BEFORE destroy: destroy() calls removeAllListeners(), so emitting
      // after it would drop the 'close' handler and never null out the caller's
      // reference — leaving the chat "open" forever and suppressing the bubble.
      this.emit('close', undefined);
      this.destroy();
    }, 300);
  }

  destroy(): void {
    if (this.chatPanel) {
      this.chatPanel.destroy();
      this.chatPanel = null;
    }
    if (this.messageHandler) {
      this.chatService.off('message:received', this.messageHandler as any);
    }
    if (this.messageUpdatedHandler) {
      this.chatService.off('message:updated', this.messageUpdatedHandler as any);
    }
    if (this.conversationsHandler) {
      this.chatService.off('conversations:updated', this.conversationsHandler as any);
    }
    this.element.remove();
    this.removeAllListeners();
  }

  // ─── Styles ─────────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .standalone-chat {
        position: absolute;
        right: 12px;
        bottom: 80px;
        width: 370px;
        max-width: calc(100vw - 24px);
        height: 420px;
        max-height: 50vh;
        z-index: 600;
        background: rgba(8, 8, 10, 0.95);
        border: 1px solid rgba(255, 0, 128, 0.2);
        border-radius: 16px;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.6), 0 0 12px rgba(255, 0, 128, 0.15);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);

        opacity: 0;
        transform: translateY(20px) scale(0.95);
        transition: opacity 0.3s ease, transform 0.3s ease;
      }

      .standalone-chat.visible {
        opacity: 1;
        transform: translateY(0) scale(1);
        pointer-events: auto;
      }

      /* ─── Header: tabs + close ─── */
      .standalone-chat-header {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 8px 44px;
        border-bottom: 1px solid rgba(255, 255, 255, 0.06);
        flex-shrink: 0;
      }

      .standalone-chat-tab {
        border: none;
        background: transparent;
        padding: 6px 12px;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 15px;
        letter-spacing: 1.5px;
        color: rgba(255, 255, 255, 0.45);
        cursor: pointer;
        border-radius: 8px;
        position: relative;
        transition: color 0.15s ease;
      }

      .standalone-chat-tab.active {
        color: #FF0080;
      }

      .standalone-chat-tab.active::after {
        content: '';
        position: absolute;
        left: 12px;
        right: 12px;
        bottom: 1px;
        height: 2px;
        border-radius: 1px;
        background: #FF0080;
        box-shadow: 0 0 6px rgba(255, 0, 128, 0.6);
      }

      .standalone-chat-close {
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        width: 28px;
        height: 28px;
        border: none;
        background: rgba(255, 255, 255, 0.06);
        color: rgba(255, 255, 255, 0.5);
        border-radius: 50%;
        font-size: 18px;
        line-height: 1;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        transition: background 0.15s ease, color 0.15s ease;
      }

      .standalone-chat-close:hover {
        background: rgba(255, 51, 102, 0.15);
        color: #FF3366;
      }

      /* ─── Content ─── */
      .standalone-chat-content {
        flex: 1;
        display: flex;
        overflow: hidden;
        min-height: 0;
      }

      .standalone-chat-chatwrap {
        flex: 1;
        display: flex;
        flex-direction: row;
        overflow: hidden;
        min-width: 0;
      }

      /* ─── Sidebar (conversation rail) ─── */
      .standalone-chat-sidebar {
        width: 52px;
        flex-shrink: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 8px 0;
        gap: 8px;
        overflow-y: auto;
        border-right: 1px solid rgba(255, 255, 255, 0.06);
      }

      /* Reuse .chat-sidebar-avatar / .chat-sidebar-badge from CrewScreen styles */

      .standalone-chat-body {
        flex: 1;
        overflow: hidden;
        display: flex;
        flex-direction: column;
        min-width: 0;
      }

      .standalone-chat-body .chat-panel {
        flex: 1;
        height: 100%;
      }

      /* ─── FAV CREW tab ─── */
      .standalone-chat-faves {
        flex: 1;
        flex-direction: column;
        gap: 2px;
        padding: 8px;
        overflow-y: auto;
        min-width: 0;
      }

      .standalone-chat-fave-row {
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        padding: 8px 10px;
        border: none;
        background: transparent;
        border-radius: 10px;
        cursor: pointer;
        text-align: left;
        transition: background 0.15s ease;
        flex-shrink: 0;
      }

      .standalone-chat-fave-row:hover {
        background: rgba(255, 255, 255, 0.05);
      }

      .standalone-chat-fave-avatar {
        width: 34px;
        height: 34px;
        border-radius: 50%;
        overflow: hidden;
        background: #242429;
        display: flex;
        align-items: center;
        justify-content: center;
        font-family: 'Bebas Neue', sans-serif;
        font-size: 15px;
        color: #FF0080;
        border: 1.5px solid rgba(255, 0, 128, 0.3);
        flex-shrink: 0;
      }

      .standalone-chat-fave-avatar img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .standalone-chat-fave-name {
        flex: 1;
        font-family: 'Rajdhani', sans-serif;
        font-weight: 600;
        font-size: 14px;
        color: #fff;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .standalone-chat-fave-online {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: #00FF88;
        box-shadow: 0 0 6px rgba(0, 255, 136, 0.6);
        flex-shrink: 0;
      }

      .standalone-chat-empty {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        text-align: center;
        padding: 20px;
        font-family: 'Rajdhani', sans-serif;
        font-weight: 500;
        font-size: 14px;
        line-height: 1.5;
        color: rgba(255, 255, 255, 0.4);
      }
    `;
    document.head.appendChild(style);
  }
}
